import { Map, View, Feature } from 'ol';
import { unByKey } from 'ol/Observable';
import { Vector as VectorLayer } from 'ol/layer';
import { ImageWMS } from 'ol/source';
import { Draw as DrawInteraction, Select as SelectInteraction, DragBox, Modify as ModifyInteraction, defaults as InteractionDefaults } from 'ol/interaction';
import { createBox } from 'ol/interaction/Draw';
import { platformModifierKeyOnly, doubleClick } from 'ol/events/condition';
import { Extent } from 'ol/extent';
import { Point, Polygon, LineString } from 'ol/geom';
import { getLength } from 'ol/sphere';
import GeometryType from 'ol/geom/GeometryType';
import * as toastr from "toastr";
import { MapLayerGroupType } from '../Models/MapConfigModels';
import { FeatureType, IFeaturesRequest, ICAMSKey, MapInteractionType, IMapInteractionConfig, TextPack } from '../Models/UtilityModels';
import * as events from '../Bus/Bus';
import { MapTrace } from './MapTrace';
import { Utilities, } from '../Utility/Utilities';
import { CAMSVectorSource } from '../../Extensions/CAMSOLExtensions';
import { MainMap } from './MainMap';


declare var G_IsMobile: Boolean;
// ----------------------------------------------------------- //
export type exportMapToPngCallback = (blob: Blob, extent: Extent) => any;

export enum OptionalTracingLayer {
    routeLineLayer,
    landPolygonLayer
}

// MAIN CLASS
export class MapInteraction {

    // ----------------------------------------------------------- //
    // GLOBAL VARS
    private mapTrace: MapTrace; 
    private mapInteractionConfig: IMapInteractionConfig = {};
    public selectInteraction: SelectInteraction;
    public selectSingleInteraction: SelectInteraction;
    private selectRouteInteraction: SelectInteraction;
    private drawInteraction: DrawInteraction;
    private modifyInteraction: ModifyInteraction;
    private selectRectangleInteraction: DragBox;
    private locationInfoEvent;
    //private selectRectangleInteraction_SelectableLayers: Array<IFeatureTypeOptions> = [{ featureType: FeatureType.Maint, visible: true }, { featureType: FeatureType.Route, visible: true }, { featureType: FeatureType.Infra, visible: true }, { featureType: FeatureType.Land, visible: true }];

    private _selectMapToleranceMobile = 20;

    // ----------------------------------------------------------- //
    //Constructor
    public constructor(protected mainMap: MainMap) {
        const self = this;        
        self.mapTrace = new MapTrace(self.mainMap.map, self.mainMap.MapStyles);
    }

    public init = (): void => {
        const self = this;
        // Create interactions
        this.selectInteraction = this.createSelectInteraction();
        this.selectSingleInteraction = this.createSelectSingleInteraction();
        this.selectRouteInteraction = this.createSelectRouteInteraction();
        this.selectRectangleInteraction = this.createSelectRectangleInteraction();
        this.modifyInteraction = this.createModifyInteraction();
    }

    // INTERACTIONS
    public removeAllInteractions = (): void => {
        this.mapInteractionConfig = {};
        this.switchMapInteraction(MapInteractionType.NoInteraction, true);
        events.bus.publish(events.eventNewMapGeometryCompleted([false, null]));
    }

    public startMultiSelection = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.selectInteraction.getFeatures().clear();
        this.mapInteractionConfig = mapInteractionConfig;
        this.switchMapInteraction(MapInteractionType.SelectInteraction, true);
    }

    public startSingleSelection = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.selectSingleInteraction.getFeatures().clear();
        this.mapInteractionConfig = mapInteractionConfig;
        var resetInteractions = mapInteractionConfig.resetAllInteractions == false ? false : true;
        this.switchMapInteraction(MapInteractionType.SelectSingleInteraction, resetInteractions);
    }

    public startRouteSelection = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.selectRouteInteraction.getFeatures().clear();
        this.mapInteractionConfig = mapInteractionConfig;
        this.switchMapInteraction(MapInteractionType.SelectRouteInteraction, true);
    }

    public startRectangleSelect = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.mapInteractionConfig = mapInteractionConfig;
        this.switchMapInteraction(MapInteractionType.SelectRectangleInteraction, true);
    }

    public startMultiAndRectangleSelect = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.mapInteractionConfig = mapInteractionConfig;
        this.switchMapInteraction(MapInteractionType.SelectMultiAndRectangleInteraction, true);
    }

    public startLocationInfo = (mapInteractionConfig: IMapInteractionConfig): void => {
        const self = this;
        // Hook up map click event
        if (self.locationInfoEvent != null) {
            unByKey(self.locationInfoEvent);
            self.locationInfoEvent = null;
        }
        self.locationInfoEvent = self.mainMap.map.on('singleclick', (event) => self.onLocationInfoEvent(event, mapInteractionConfig));
    }

    public startAdditionalLayersLocationInfo = (layer): void => {
        const self = this;
        // Hook up map click event
        if (self.locationInfoEvent != null) {
            unByKey(self.locationInfoEvent);
            self.locationInfoEvent = null;
        }

        self.locationInfoEvent = self.mainMap.map.on('singleclick', (event) => self.onAdditionalLayerLocationInfoEvent(layer, event));
    }

    public startDrawSelection = (type: GeometryType, minZoom: number, parentFeature: Feature, parentMinDistance: number, tracingLayer: OptionalTracingLayer = null): void => {
        const self = this;
        this.mapInteractionConfig = {}
   
        // Check on varous conditions when a point is added
        function drawCondition(e) {
            if (minZoom != null && self.mainMap.map.getView().getZoom() < minZoom) {
                Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_ZoomCloserError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
                return false;
            }

            if (parentFeature != null && parentMinDistance!= null) {
                var c = parentFeature.getGeometry().getClosestPoint(e.coordinate);
                var line = new LineString([e.coordinate, c]);
                var distance = getLength(line)      
                var isInsidePolygon = parentFeature.getGeometry().intersectsCoordinate(e.coordinate);

                if (distance > parentMinDistance && isInsidePolygon != true) {
                    Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_PlacePointCloserError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
                    return false;
                }
            }
            return true;
        }

        if (self.drawInteraction) {
            self.drawInteraction.un('drawend', null);
            self.switchMapInteraction(MapInteractionType.NoInteraction, true);
            self.drawInteraction = null;
        }

        if (type == GeometryType.CIRCLE) {
            self.drawInteraction = new DrawInteraction({
                source: self.mainMap.editLayer.getSource(),
                type: type,
                condition: drawCondition,
                geometryFunction: createBox()
            });
        }
        else {
            self.drawInteraction = new DrawInteraction({
                source: self.mainMap.editLayer.getSource(),
                type: type,
                condition: drawCondition,
                //snapTolerance: 4
            });
        }
        //self.drawInteraction.Name = "DrawInteraction";

        self.drawInteraction.on('drawend', function (e: any) {
            events.bus.publish(events.eventDrawCompelted(e.feature));
        });

        this.switchMapInteraction(MapInteractionType.DrawInteraction, true, tracingLayer);
    }

    public undoLastPoint = (): void => {
        const self = this;
        if (self.drawInteraction != null) {
            self.drawInteraction.removeLastPoint();
        }
        else if (self.modifyInteraction != null) {
            self.modifyInteraction.removePoint();
        }
    }

    private createSelectInteraction = (): SelectInteraction => {
        const self = this;
        var tol = self.mainMap.mapConfigModel.mapInitialState.selectHitTolerance as number;
        if (G_IsMobile == true) this._selectMapToleranceMobile = 20;
        const si = new SelectInteraction({
            hitTolerance: tol,
            filter: self.canSelectFeatureFunction,
            //style: false,
            multi: true
        });
        //si.Name = "SelectInteraction";

        si.on('select', function (e: any) {
            if (e.selected.length > 0) {
                self.processFeatureSelectionResults(e.selected);
                events.bus.publish(events.eventMultipleFeaturesSelected({ features: self.mainMap.selectedFeaturesList, point: null }));
                self.selectInteraction.getFeatures().clear();
            }
        });

        return si;
    }

    private createSelectSingleInteraction = (): SelectInteraction => {
        const self = this;
        var tol = self.mainMap.mapConfigModel.mapInitialState.selectHitTolerance as number;
        if (G_IsMobile == true) this._selectMapToleranceMobile = 20;
        const si = new SelectInteraction({
            hitTolerance: tol,
            filter: self.canSelectFeatureFunction,
            //style: false,
            multi: false
        });
        //si.Name = "SelectSingleInteraction";

        si.on('select', function (e: any) {
            if (e.selected.length > 0) {
                self.processFeatureSelectionResults([e.selected[0]]);
                events.bus.publish(events.eventSingleFeatureSelected(e.selected[0]));
                self.selectSingleInteraction.getFeatures().clear();
            }
        });

        return si;
    }

    private createSelectRouteInteraction = (): SelectInteraction => {
        const self = this;
        const si = new SelectInteraction({
            filter: self.canSelectFeatureFunction,
            hitTolerance: 10
            //style: false
        });
        //si.Name = "SelectRouteInteraction";

        si.on('select', function (e: any) {
            if (e.selected.length > 0) {
                self.processFeatureSelectionResults(e.selected);
                events.bus.publish(events.eventRouteSelected(e.selected[0]));
            }
        });

        return si;
    }

    private createSelectRectangleInteraction = (): DragBox => {
        const self = this;
        const si = new DragBox({ condition: platformModifierKeyOnly });
        //si.Name = "SelectRectangleInteraction";
    
        si.on('boxend', function () {
            let candidateFeatures = [];
            var extent = self.selectRectangleInteraction.getGeometry().getExtent();

            function tryInteraction(o) {
                try
                {
                    if (o == null) return;
                    var s = o.getSource();
                    if (s == null || s.forEachFeatureIntersectingExtent == null) return;
                    s.forEachFeatureIntersectingExtent(extent, function (feature) { if (feature != null) candidateFeatures.push(feature); });
                }
                catch (e)
                {
                    console.error(e);
                }
            }

            // Get any intersecting features
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Maint) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Maint).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Infra) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Infra).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.InfraHistoric) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.InfraHistoric).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.LegalEvent) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalRoute).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.LegalFeature) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalFeature).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Route) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Route).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Path) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Route).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Land) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Land).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Annotation) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Annotation).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }
            if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.TRO) != null) {
                self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.TRO).forEach(m => {
                    m.olLayers.forEach(o => { tryInteraction(o); });  
                });
            }

            // Then add them to the selection group
            if (candidateFeatures.length > 0) {
                candidateFeatures = self.processFeatureSelectionResults(candidateFeatures);

                let query: IFeaturesRequest = { maintCodes: [], infraCodes: [], infraHistoricCodes: [], routeCodes: [], landCodes: [], annotationGeomIDs: [], troGeomIDs: [], legalRoutes: [], legalFeatures: [] };
                candidateFeatures.forEach(f => {
                    var p = f.getProperties();
                    if (p.MaintCode != null) {
                        query.maintCodes.push(p.MaintCode);
                    }
                    else if (p.InfraCode != null) {
                        query.infraCodes.push(p.InfraCode);
                        query.infraCodes.push(p.InfraCode);
                    }
                    else if (p.RouteCode != null) {
                        query.routeCodes.push(p.RouteCode);
                    }
                    else if (p.LegalRoute != null) {
                        query.legalRoutes.push(p.LegalRoute);
                    }
                    else if (p.LegalFeature != null) {
                        query.legalFeatures.push(p.LegalFeature);
                    }
                    else if (p.LandCode != null) {
                        query.landCodes.push(p.LandCode);
                    }
                    else if (p.AnnotationGeomID != null) {
                        query.annotationGeomIDs.push(p.AnnotationGeomID);
                    }
                    else if (p.TROGeomID != null) {
                        query.troGeomIDs.push(p.LandCode);
                    }
                });

                events.bus.publish(events.eventRectangleSelectionComplete(query as any));
            }
        });

        return si;
    }

    private createModifyInteraction = (): ModifyInteraction => {
        const self = this;

        // Add draw interaction
        const modifyInteraction = new ModifyInteraction({
            source: self.mainMap.editLayer.getSource(),
            deleteCondition: doubleClick
            //deleteCondition: function (event) {
            //    var key = event.originalEvent.keycode || event.originalEvent.charCode;
            //    console.log(key);
            //    if (key == 46) {
            //        return always(event);
            //    } else {
            //        return never(event);
            //    }
            //}
        })
        //modifyInteraction.Name = "ModifyInteraction";

        return modifyInteraction;
    }

    private processFeatureSelectionResults = (candidateFeatures: Array<Feature>): Array<Feature> => {
        const self = this;
        let removeCAMSKeys: Array<ICAMSKey> = [];

        // Then add them to the selection group
        if (candidateFeatures.length > 0) {
            // First remove any duplicates from the incoming list
            candidateFeatures = this.removeDuplicateFeaturesFromList(candidateFeatures);

            if (self.mainMap.selectedFeaturesList == null) self.mainMap.selectedFeaturesList = [];
            if (self.mapInteractionConfig.addToExistingSelection == true) {
                // Go through each feature and add/remove to the selectedfeatures list
                let candidateFeatures_copy = [...candidateFeatures];
                candidateFeatures.forEach((a, i) => {
                    var f = a as any;
                    let index = this.getIndexOfFeatureByEntityCode(a, self.mainMap.selectedFeaturesList);
                    if (index < 0) {
                        self.mainMap.selectedFeaturesList.push(f);
                    }
                    else {
                        removeCAMSKeys.push(Utilities.convertFeatureToCAMSKey(self.mainMap.selectedFeaturesList[index]));
                        self.mainMap.selectedFeaturesList.splice(index, 1);
                        let copyIndex = this.getIndexOfFeatureByEntityCode(a, candidateFeatures_copy);
                        candidateFeatures_copy.splice(copyIndex, 1); // Splice from the copy
                    }
                });
                if (removeCAMSKeys.length > 0) events.bus.publish(events.eventUnselectedFeatures({ keys: removeCAMSKeys }));
                candidateFeatures = [...candidateFeatures_copy]; // Dont forget to copy it back
            }
            else {
                self.mainMap.selectedFeaturesList = candidateFeatures;
            }

            self.mainMap.forceReloadSelectedFeaturesLayer();
            if (self.mapInteractionConfig.zoomToSelectedFeatures == true) self.mainMap.MapZoom.centreMapOnFeatures(self.mainMap.selectedFeaturesList);
        }

        return candidateFeatures;
    }

    private onLocationInfoEvent = (evt, mapInteractionConfig: IMapInteractionConfig): void => {
        const self = this;
    
        // Clear points
        this.mainMap.clearAllHighlights();

        // Place a new point on the map
        let point = new Point(evt.coordinate);
        let marker = new Feature(point);
        
        // Create a small extent around the point
        var tol = (mapInteractionConfig.hitTolerance ? mapInteractionConfig.hitTolerance : 7);
        if (G_IsMobile == true) this._selectMapToleranceMobile = 20;
     
        let extent = this.createExtentFromPointUsingPixels(evt.coordinate, this.mainMap.map, tol);

        // Select features in the vicinity
        let features = [];
       
        let callback = function (ft) {
            features.push(ft);
            return false;
        }
       
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Maint) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Maint).forEach(m => {
                m.olLayers?.forEach(o => {                   
                    if (o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.MyMaint).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Infra) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Infra).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.InfraHistoric) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.InfraHistoric).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.LegalEvent) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalRoute).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.LegalFeature) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalFeature).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Route) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Route).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Land) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Land).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Annotation) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Annotation).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }
        if (mapInteractionConfig.selectableLayers.find(i => i == FeatureType.TRO) != null) {
            self.mainMap.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.TRO).forEach(m => {
                m.olLayers?.forEach(o => {
                    if (o && o.getVisible() == true) o.getSource()?.forEachFeatureIntersectingExtent(extent, callback);
                });
            });
        }

        // Zoom to location
        if (mapInteractionConfig.zoomToSelectedFeatures == true) this.mainMap.MapZoom.centreMapOnFeatures([marker], this.mainMap.map.getView().getZoom());

        // Show the overlay
        $('.ol-popup').css('height', '450px'); // Set this to the max-height of the popup. Then OL will autopan correctly
        var coordinate = evt.coordinate;
        this.mainMap.mapOverlay.setPosition(coordinate);
        $('#mainMapPopupClose').on('click', function () {
            self.mainMap.mapOverlay.setPosition(undefined);
            $(this).blur();
            return false;
        });

        // Fire event
        events.bus.publish(events.eventMultipleFeaturesSelected({ features: features, point: (point as any).flatCoordinates }));
    }

    private onAdditionalLayerLocationInfoEvent = (layer, evt): void => {
        const self = this;

        // Show the overlay
        $('#mainMapPopupContainer').html("");
        Utilities.BlockUI($('#mainMapPopup'));
        $('.ol-popup').css('height', '300px'); // Set this to the max-height of the popup. Then OL will autopan correctly
        var coordinate = evt.coordinate;
        this.mainMap.mapOverlay.setPosition(coordinate);
        $('#mainMapPopupContainer').scrollTop(0);
        $('#mainMapPopupClose').on('click', function () {
            self.mainMap.mapOverlay.setPosition(undefined);
            $(this).blur();
            return false;
        });

        if (layer.getSource() instanceof CAMSVectorSource) {
            let extent = this.createExtentFromPointUsingPixels(evt.coordinate, this.mainMap.map,7);
            var wfsSource: CAMSVectorSource = layer.getSource() as any;
            wfsSource.forEachFeatureIntersectingExtent(extent, function (f) {
                if (f != null) {
                    var html = "<div style='height: 240px; font-size: 0.8rem;'>";
                    var style = "font-weight: 600; width: 40%; margin-right: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;";
                    var att = f.getProperties();
                    var bg = true;
                    for (var k in att) {
                        if ((att[k] instanceof Object) == false) {
                            var bgC = ""; bg = !bg; if (bg == true) bgC = "background-color: lightgray;";
                            html += `<div class="display: flex;"><div style="${bgC} display: flex;" class="p-1"><span style="${style}">${k}:</span>${att[k]}</div></div>`;
                        }
                    }
                    html += "</div>";
                    $('#mainMapPopupContainer').html(html);
                }
            });
            Utilities.UnBlockUI($('#mainMapPopup'));
        }
        else {
            const viewResolution = self.mainMap.map.getView().getResolution();
            var wmsSource: ImageWMS = layer.getSource() as any;
            var params = wmsSource.getParams();
            const url = wmsSource.getFeatureInfoUrl(
                evt.coordinate,
                viewResolution,
                'EPSG:27700',
                { 'INFO_FORMAT': 'text/html', 'QUERY_LAYERS': params['layers'] }
            );

            if (url) {
                fetch(url)
                    .then((response) => response.text())
                    .then((html) => {
                        Utilities.UnBlockUI($('#mainMapPopup'));
                        $('#mainMapPopupContainer').html(html);
                    });
            }
        }

        // Clear points
        this.mainMap.clearAllHighlights();

        // Place a new point on the map
        let point = new Point(evt.coordinate);
        let marker = new Feature(point);

        // Zoom to location
        //this.centreMapOnFeatures([marker], this.map.getView().getZoom());
    }

    public switchMapInteraction = (interactionType: MapInteractionType, resetAll: boolean, tracingLayer: OptionalTracingLayer = null): void => {
        // Get the interaction
        let interaction;
        const self = this;
        var list = [this.selectInteraction, this.selectRectangleInteraction, this.selectSingleInteraction, this.selectRouteInteraction, this.drawInteraction, this.modifyInteraction]

        // Always sto the tracing
        self.mapTrace.stopTracing();

        switch (interactionType) {
            case MapInteractionType.SelectInteraction: {
                interaction = this.selectInteraction;
                if (resetAll == true) removeAll();
                self.mainMap.map.addInteraction(interaction);
                break;
            }
            case MapInteractionType.SelectSingleInteraction: {
                interaction = this.selectSingleInteraction;
                if (resetAll == true) removeAll();
                self.mainMap.map.addInteraction(interaction);
                break;
            }
            case MapInteractionType.SelectRouteInteraction: {
                interaction = this.selectRouteInteraction;
                if (resetAll == true) removeAll();
                self.mainMap.map.addInteraction(interaction);
                break;
            }
            case MapInteractionType.SelectRectangleInteraction: {
                interaction = this.selectRectangleInteraction;
                if (resetAll == true) removeAll();
                self.mainMap.map.addInteraction(interaction);
                break;
            }
            case MapInteractionType.SelectMultiAndRectangleInteraction: {
                if (resetAll == true) removeAll();
                self.mainMap.map.addInteraction(this.selectInteraction);
                self.mainMap.map.addInteraction(this.selectRectangleInteraction);
                break;
            }
            case MapInteractionType.DrawInteraction: {
                interaction = this.drawInteraction;
                if (resetAll == true) removeAll();
 
                // If a tracing layer is specified, follow this process for Lines and Polygons. If not, just do the normal thing
                if ((interaction.type_ == GeometryType.LINE_STRING || interaction.type_ == GeometryType.POLYGON) && tracingLayer != null) {
                    switch (tracingLayer) {
                        case OptionalTracingLayer.routeLineLayer:
                            var routeLineLayer = self.mainMap.mapConfigModel.coreLayers.find(f => f.mapLayerGroupType == MapLayerGroupType.Route).olLayers[0];
                            self.mapTrace.startTracingNewFeature(routeLineLayer, self.drawInteraction, OptionalTracingLayer.routeLineLayer);
                            break;
                        case OptionalTracingLayer.landPolygonLayer:
                            var landPolyLayer = self.mainMap.mapConfigModel.coreLayers.find(f => f.mapLayerGroupType == MapLayerGroupType.Land).olLayers[0];
                            self.mapTrace.startTracingNewFeature(landPolyLayer, self.drawInteraction, OptionalTracingLayer.landPolygonLayer);
                            break;
                    }
                }
                else {
                    self.mainMap.map.addInteraction(interaction);
                }
                break;
            }
            case MapInteractionType.ModifyInteraction: {
                interaction = this.modifyInteraction;
                if (resetAll == true) removeAll();
                self.mainMap.map.addInteraction(interaction);
                break;
            }
            case MapInteractionType.NoInteraction: {
                removeAll();
                break;
            }
            default:
                removeAll();
                break;
        }

        function removeAll() {
            list.forEach(i => self.mainMap.map.removeInteraction(i));
            if (self.locationInfoEvent != null) {
                unByKey(self.locationInfoEvent);
                self.locationInfoEvent = null;
            }
        }
    }

    // ----------------------------------------------------------- //

    // ----------------------------------------------------------- //
    // Modify
    public modifyFeature = (data: ICAMSKey, showPanel: Boolean = true): void => {
        let feature = this.mainMap.getFeatureFromMap(data);
        if (feature != null) {
            // Unselect all features
            let selectedCollection = this.selectInteraction.getFeatures();
            this.mainMap.highlightedFeaturesList = [];
            selectedCollection.clear();
            this.mainMap.forceReloadHighlightLayer();

            // Hide the other panels, careful as this has to happen now and not after the ModifyInteraction is added
            events.bus.publish(events.eventResetAllPanels());
            events.bus.publish(events.eventResetAllButtons(false));

            // Copy feature to edit layer
            this.mainMap.editLayer.getSource().clear();
            this.mainMap.editLayer.getSource().addFeature(feature);
            this.switchMapInteraction(MapInteractionType.ModifyInteraction, true);

            if (showPanel == true) { 
                // Popup the save/cancel panel
                if (feature.getGeometry().getType() == "Point") {
                    $('#mapButtonsPointMsg').show();
                    $('#mapButtonsLinePolyMsg').hide();
                }
                else {
                    $('#mapButtonsPointMsg').hide();
                    $('#mapButtonsLinePolyMsg').show();
                }
                $('#mapButtonsPanel').addClass('show');
            }
        }
    }

    // ****************************************************************** //
    //  PRIVATE
    // ****************************************************************** //
    private createExtentFromPointUsingPixels = (coordinate, map: Map, hitTolerance: number): Extent => {
        // Create a rectangle around the point and return its extent
        let pixel = map.getPixelFromCoordinate(coordinate);
        let TL = map.getCoordinateFromPixel([pixel[0] - hitTolerance, pixel[1] + hitTolerance]);
        let TR = map.getCoordinateFromPixel([pixel[0] + hitTolerance, pixel[1] + hitTolerance]);
        let BL = map.getCoordinateFromPixel([pixel[0] - hitTolerance, pixel[1] - hitTolerance]);
        let BR = map.getCoordinateFromPixel([pixel[0] + hitTolerance, pixel[1] - hitTolerance]);
        let points = [[TL, TR, BR, BL]];
        let poly = new Polygon(points);

        return poly.getExtent();
    }

    private canSelectFeatureFunction = (feature: Feature, layer: VectorLayer): boolean => {
        const self = this;

        // Get layer name
        let layerName = layer.get("layerName");
        if (layerName == null || layerName == "") return false;

        // Get config
        var coreLayerConfig = self.mainMap.mapConfigModel.coreLayers.find(c => c.wfsPointName == layerName || c.wfsLineName == layerName || c.wfsPolyName == layerName);
        if (coreLayerConfig == null) return false;

        // Check config type
        switch (coreLayerConfig.mapLayerGroupType) {
            case MapLayerGroupType.Maint:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Maint) != null) return true;
                break;
            case MapLayerGroupType.Infra:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Infra) != null) return true;
                break;
            case MapLayerGroupType.Route:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Route) != null) return true;
                break;
            case MapLayerGroupType.Land:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Land) != null) return true;
                break;
            case MapLayerGroupType.LegalRoute:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.LegalEvent) != null) return true;
                break;
            case MapLayerGroupType.LegalFeature:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.LegalFeature) != null) return true;
                break;
            case MapLayerGroupType.InfraHistoric:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.InfraHistoric) != null) return true;
                break;
            case MapLayerGroupType.Annotation:
                if (self.mapInteractionConfig.selectableLayers.find(i => i == FeatureType.Annotation) != null) return true;
                break;
        }

        return false;
    }

    private getIndexOfFeatureByEntityCode = (feature: Feature, features: Feature[]): number => {
        const self = this;
        let f = feature as any;

        let index = features.findIndex(b => {
            if (b == null) return false;

            var s = b as any;
            if (s.values_.RouteCode && s.values_.RouteCode == f.values_.RouteCode) return true;
            if (s.values_.InfraCode && s.values_.InfraCode == f.values_.InfraCode) return true;
            if (s.values_.LegalRoute && s.values_.LegalRoute == f.values_.LegalRoute) return true;
            if (s.values_.LegalFeature && s.values_.LegalFeature == f.values_.LegalFeature) return true;
            if (s.values_.MaintCode && s.values_.MaintCode == f.values_.MaintCode) return true;
            if (s.values_.LandCode && s.values_.LandCode == f.values_.LandCode) return true;
            if (s.values_.AnnotationGeomID && s.values_.AnnotationGeomID == f.values_.AnnotationGeomID) return true;
            if (s.values_.TROGeomID && s.values_.TROGeomID == f.values_.TROGeomID) return true;
            return false;
        });

        return index
    }

    private removeDuplicateFeaturesFromList = (features: Feature[]): Array<Feature> => {
        const self = this;
        let results: Array<Feature> = []

        features.forEach((f,i) => {
            var index = this.getIndexOfFeatureByEntityCode(f, results);
            if (index < 0) results.push(features[i]);
        });
        return results;
    }
    
}




